// PIN shape and generation. Pure and isomorphic — no server imports, because
// the admin form generates a PIN locally from the synced `assignedPins` set.

export const PIN_PATTERN = /^\d{4}$/
const PIN_SPACE = 10_000

export function isValidPin(value: string): boolean {
  return PIN_PATTERN.test(value)
}

function fourDigits(value: number): string {
  return String(value).padStart(4, '0')
}

/**
 * Draw an unused four-digit PIN.
 *
 * `taken` comes from `assignedPins`, which carries every PIN held by a member —
 * including soft-deleted ones, since a PIN stays reserved once issued. It
 * cannot include the reserved kiosk PIN, which is deliberately never synced, so
 * roughly one draw in ten thousand is rejected by the `pins` primary key on the
 * server and the caller redraws.
 */
export function generatePin(taken: ReadonlySet<string>): string {
  if (taken.size >= PIN_SPACE) {
    throw new Error('every four-digit PIN is already in use')
  }

  // Random probing, then an exhaustive sweep from a random offset so a nearly
  // full space still terminates instead of spinning on collisions.
  const random = new Uint32Array(1)
  for (let attempt = 0; attempt < 64; attempt++) {
    crypto.getRandomValues(random)
    const candidate = fourDigits(random[0] % PIN_SPACE)
    if (!taken.has(candidate)) return candidate
  }

  crypto.getRandomValues(random)
  const start = random[0] % PIN_SPACE
  for (let offset = 0; offset < PIN_SPACE; offset++) {
    const candidate = fourDigits((start + offset) % PIN_SPACE)
    if (!taken.has(candidate)) return candidate
  }

  throw new Error('every four-digit PIN is already in use')
}
